import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Chip, CircularProgress, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { publicInternshipService } from '../services/api';
import { InternshipDetail, STATUS_META, fmtDate } from '../components/internshipTypes';
import { DecisionTab, GoalsTab, OverviewTab, ReviewsTab } from '../components/InternshipDetailDialog';

const noop = async () => false;

// Public, login-free, read-only view of one internship for an outside evaluator. Reached only via
// the opaque link HR/the mentor generates in InternshipDetailDialog. The server already removed
// stipend, HR notes, conversion salary and the intern's email; this page only renders.
const SharedInternship: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [detail, setDetail] = useState<InternshipDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!token) return;
    publicInternshipService.get(token)
      .then(setDetail)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 1, sm: 2 }, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Paper sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1100, width: '100%', alignSelf: 'flex-start', mt: { xs: 1, sm: 3 } }}>
        {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}
        {notFound && <Alert severity="error">This link is invalid, or sharing has been turned off. Please ask the sender for a new link.</Alert>}
        {detail && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 0.5 }}>
              <Typography variant="h5">{detail.internName}</Typography>
              <Typography variant="body1" color="text.secondary">{detail.roleTitle}</Typography>
              <Chip size="small" label={STATUS_META[detail.status]?.label ?? detail.status} color={STATUS_META[detail.status]?.color ?? 'default'} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Internship evaluation — read-only shared view
            </Typography>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
              <Tab label="Overview" />
              <Tab label={`Check-ins (${detail.reviews.filter(r => r.status === 'Submitted').length}/${detail.reviews.length})`} />
              <Tab label={`Goals (${detail.goals.length})`} />
              <Tab label="Evaluation & Decision" />
              <Tab label={`Certificates (${detail.certificates.length})`} />
            </Tabs>

            {tab === 0 && <OverviewTab detail={detail} isAdmin={false} onEdit={() => undefined} />}
            {tab === 1 && <ReviewsTab detail={detail} canMentor={false} onReview={() => undefined} onShare={() => undefined} />}
            {tab === 2 && <GoalsTab detail={detail} readOnly />}
            {tab === 3 && <DecisionTab detail={detail} isAdmin={false} run={noop} />}
            {tab === 4 && (
              detail.certificates.length === 0
                ? <Typography color="text.secondary">No certificate issued.</Typography>
                : (
                  <Stack spacing={1}>
                    {detail.certificates.map(c => (
                      <Paper key={c.id} variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2">{c.certificateNo} · {c.certificateType}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {fmtDate(c.startDate)} – {fmtDate(c.endDate)} · issued {fmtDate(c.issuedAt)}
                          {c.isRevoked ? ' · REVOKED' : ''}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                )
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SharedInternship;
